import { FC } from 'react';
import { Mail, Building2, Calendar, Clock, Edit2, Save } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { UserProfile, ProfileFormData, FormErrors } from '../types';
import { AvatarUpload } from './AvatarUpload';

interface ProfileTabProps {
    profile: UserProfile;
    isEditing: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    isSaving: boolean;
    formData: ProfileFormData;
    formErrors: FormErrors;
    onInputChange: (field: keyof ProfileFormData, value: string) => void;
    onProfileUpdate: (profile: UserProfile) => void;
}

export const ProfileTab: FC<ProfileTabProps> = ({
    profile,
    isEditing,
    onEdit,
    onCancel,
    onSave,
    isSaving,
    formData,
    formErrors,
    onInputChange,
    onProfileUpdate,
}) => {
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    return (
        <>
            {/* Header with Edit Button */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
                    <p className="text-sm text-gray-500 mt-1">Update your personal details and bio</p>
                </div>
                {!isEditing ? (
                    <Button onClick={onEdit} variant="secondary" size="md">
                        <Edit2 className="w-4 h-4" />
                        <span className="ml-2">Edit</span>
                    </Button>
                ) : (
                    <div className="flex gap-3">
                        <Button onClick={onCancel} variant="ghost" size="md" disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={onSave}
                            variant="primary"
                            size="md"
                            isLoading={isSaving}
                            disabled={isSaving || Object.values(formErrors).some((error) => error !== undefined)}
                        >
                            <Save className="w-4 h-4" />
                            <span className="ml-2">Save Changes</span>
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sidebar - Profile Summary */}
                <div className="lg:col-span-1">
                    <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-6">
                        <AvatarUpload
                            profile={profile}
                            onProfileUpdate={onProfileUpdate}
                            onError={(msg) => console.error(msg)} // TODO: Handle error better (toast?)
                        />

                        {/* Divider */}
                        <div className="w-full border-t border-gray-200 my-6"></div>

                        {/* Quick Stats */}
                        <div className="w-full space-y-4 text-left">
                            <div className="flex items-center gap-3 text-sm">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <Mail className="w-4 h-4 text-gray-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="text-sm text-gray-900 truncate">{profile.email}</p>
                                </div>
                            </div>

                            {profile.company && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <Building2 className="w-4 h-4 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-500">Company</p>
                                        <p className="text-sm text-gray-900 truncate">{profile.company}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content - Profile Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Personal Information */}
                    <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-base font-semibold text-gray-900">Personal Information</h3>
                        </div>
                        <div className="p-6 space-y-5">
                            {/* Full Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                {isEditing ? (
                                    <div>
                                        <Input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => onInputChange('name', e.target.value)}
                                            placeholder="Enter your full name"
                                            required
                                            className={formErrors.name ? 'border-red-500' : ''}
                                        />
                                        {formErrors.name && <p className="text-xs text-red-600 mt-1.5">{formErrors.name}</p>}
                                    </div>
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-900">{profile.name}</div>
                                )}
                            </div>

                            {/* Email (Read-only) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 rounded-md border border-gray-200">
                                    {profile.email}
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5">Email address cannot be modified</p>
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                {isEditing ? (
                                    <div>
                                        <Input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => onInputChange('phone', e.target.value)}
                                            placeholder="Enter your phone number"
                                            className={formErrors.phone ? 'border-red-500' : ''}
                                        />
                                        {formErrors.phone && <p className="text-xs text-red-600 mt-1.5">{formErrors.phone}</p>}
                                    </div>
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-900">
                                        {profile.phone || <span className="text-gray-400">Not provided</span>}
                                    </div>
                                )}
                            </div>

                            {/* Company */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                                {isEditing ? (
                                    <div>
                                        <Input
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => onInputChange('company', e.target.value)}
                                            placeholder="Enter your company name"
                                            className={formErrors.company ? 'border-red-500' : ''}
                                        />
                                        {formErrors.company && <p className="text-xs text-red-600 mt-1.5">{formErrors.company}</p>}
                                    </div>
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-900">
                                        {profile.company || <span className="text-gray-400">Not provided</span>}
                                    </div>
                                )}
                            </div>

                            {/* Bio */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                                {isEditing ? (
                                    <div>
                                        <textarea
                                            value={formData.bio}
                                            onChange={(e) => onInputChange('bio', e.target.value)}
                                            placeholder="Tell us about yourself"
                                            rows={4}
                                            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none ${formErrors.bio ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                        <div className="flex items-center justify-between mt-1.5">
                                            <div>{formErrors.bio && <p className="text-xs text-red-600">{formErrors.bio}</p>}</div>
                                            <p className={`text-xs ${formData.bio.length > 500 ? 'text-red-600' : 'text-gray-500'}`}>
                                                {formData.bio.length}/500
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-700 min-h-[88px] whitespace-pre-wrap">
                                        {profile.bio || <span className="text-gray-400">No bio provided</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Account Activity */}
                    <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-base font-semibold text-gray-900">Account Activity</h3>
                            <p className="text-sm text-gray-500 mt-1">Your account timeline and activity</p>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* Member Since */}
                                <div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                        <Calendar className="w-4 h-4" />
                                        <span>Member Since</span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-900">{formatDate(profile.createdAt)}</p>
                                </div>

                                {/* Last Login */}
                                {profile.lastLogin && (
                                    <div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                            <Clock className="w-4 h-4" />
                                            <span>Last Login</span>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">{formatDate(profile.lastLogin)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
